import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import Loader from "react-loader-spinner";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { getInsuranceClaim } from "../../../actions/complaintsDataAction";
import "./InsuranceDetails.css";

// NAS-6091 — read-only SYMBO claim status tab. Lazy-fetches on mount (mirrors the History tab),
// reads the result off complaintsDetail.insurance. Colour is driven by the normalised `family`
// the backend returns; the whole tab is dark-native and scoped under .insurance-tab.
const FAMILY = {
  pending: { accent: "#f59e0b", soft: "rgba(245,158,11,0.18)", bg: "rgba(245,158,11,0.08)" },
  in_progress: { accent: "#60a5fa", soft: "rgba(96,165,250,0.18)", bg: "rgba(96,165,250,0.08)" },
  approved: { accent: "#34d399", soft: "rgba(52,211,153,0.18)", bg: "rgba(52,211,153,0.08)" },
  rejected: { accent: "#f87171", soft: "rgba(248,113,113,0.18)", bg: "rgba(248,113,113,0.08)" },
  warehouse: { accent: "#a78bfa", soft: "rgba(167,139,250,0.18)", bg: "rgba(167,139,250,0.08)" },
};

class InsuranceDetails extends Component {
  state = { isLoading: true, copied: false };

  componentDidMount() {
    this.load(false);
  }

  load = async (refresh) => {
    const detail = this.props.complaintDetail;
    if (!detail || !detail.id) {
      this.setState({ isLoading: false });
      return;
    }
    this.setState({ isLoading: true });
    await this.props.getInsuranceClaim(detail.id, refresh);
    this.setState({ isLoading: false });
  };

  renderInfo = (message) => (
    <div className="insurance-tab">
      <div className="ins-info">
        {message}
        <div className="ins-info-actions">
          <button className="ins-btn" onClick={() => this.load(true)}>
            <i className="fas fa-sync-alt" /> Refresh
          </button>
        </div>
      </div>
    </div>
  );

  render() {
    const { isLoading, copied } = this.state;
    const insurance =
      (this.props.complaintDetail && this.props.complaintDetail.insurance) || null;

    if (isLoading) {
      return (
        <div className="insurance-tab" style={{ textAlign: "center", padding: "40px 0" }}>
          <Loader type="Oval" color="#60a5fa" height={64} width={64} />
          <p style={{ marginTop: 12, color: "#9aa3b2" }}>Fetching claim status…</p>
        </div>
      );
    }

    if (!insurance) return this.renderInfo("Could not reach SYMBO right now. Please retry.");
    if (!insurance.linked) return this.renderInfo("No insurance claim is linked to this ticket.");
    if (!insurance.found) return this.renderInfo(insurance.message || "No claim found for this ticket.");

    const banner = insurance.banner || {};
    const claim = insurance.claim || {};
    const timeline = insurance.timeline || [];
    const footer = insurance.partner_footer || {};
    const fam = FAMILY[banner.family] || FAMILY.in_progress;

    return (
      <div className="insurance-tab">
        {/* Header */}
        <div className="ins-head">
          <p className="ins-title">Insurance Claim</p>
          <button className="ins-btn" onClick={() => this.load(true)}>
            <i className="fas fa-sync-alt" /> Refresh
          </button>
        </div>

        {/* Status banner */}
        <div
          className="ins-banner"
          style={{ backgroundColor: fam.bg, borderLeftColor: fam.accent }}
        >
          <div className="ins-banner-top">
            <span className="ins-state" style={{ color: fam.accent }}>
              {banner.state}
            </span>
            {banner.raw_code && <span className="ins-chip ins-chip-code">{banner.raw_code}</span>}
            {banner.owner && (
              <span className="ins-chip ins-chip-owner">Action with: {banner.owner}</span>
            )}
          </div>
          {insurance.from_cache && insurance.as_of && (
            <p className="ins-asof">
              As of {moment(insurance.as_of).format("MMM DD, YYYY · hh:mm A")}
            </p>
          )}
          {banner.script && (
            <div className="ins-script">
              <span className="ins-script-label">What to tell the customer</span>
              {banner.script}
            </div>
          )}
        </div>

        {/* Rejection reason (rejected states only) */}
        {banner.rejection_reason && (
          <div className="ins-rejection">
            <span className="ins-rejection-label">Rejection reason</span>
            <p className="ins-rejection-text">{banner.rejection_reason}</p>
          </div>
        )}

        {/* Claim summary */}
        <div className="ins-grid">
          <div className="ins-field">
            <span className="ins-label">Claim ID</span>
            <p className="ins-value">
              {claim.claim_id || "—"}
              {claim.claim_id && (
                <CopyToClipboard
                  text={claim.claim_id}
                  onCopy={() => {
                    this.setState({ copied: true });
                    setTimeout(() => this.setState({ copied: false }), 1500);
                  }}
                >
                  <i className="fas fa-copy ins-copy" title="Copy claim ID" />
                </CopyToClipboard>
              )}
              {copied && <span className="ins-copied">copied</span>}
            </p>
          </div>
          <div className="ins-field">
            <span className="ins-label">Invoice Number</span>
            <p className="ins-value">{claim.invoice_number || "—"}</p>
          </div>
          <div className="ins-field">
            <span className="ins-label">Device MAC</span>
            <p className="ins-value">{claim.mac_id || "—"}</p>
          </div>
          <div className="ins-field">
            <span className="ins-label">Claim Raised On</span>
            <p className="ins-value">{claim.raised_at || "—"}</p>
          </div>
          <div className="ins-field">
            <span className="ins-label">Last Updated</span>
            <p className="ins-value">{claim.status_updated_at || "—"}</p>
          </div>
          <div className="ins-field">
            <span className="ins-label">Earlier Claims</span>
            <p className="ins-value">
              {claim.earlier_claims_count > 0
                ? `${claim.earlier_claims_count} on this device`
                : "None"}
            </p>
          </div>
        </div>

        {/* Claim timeline */}
        {timeline.length > 0 && (
          <div>
            <p className="ins-section-title">Claim Timeline</p>
            <div className="ins-timeline">
              {timeline.map((ev, idx) => {
                const isCurrent = idx === timeline.length - 1;
                return (
                  <div
                    key={idx}
                    className={`ins-tl-item${isCurrent ? " is-current" : ""}`}
                    style={isCurrent ? { "--ins-accent": fam.accent, "--ins-accent-soft": fam.soft } : undefined}
                  >
                    <p className="ins-tl-meta">
                      {ev.date}
                      {ev.owner ? ` · ${ev.owner}` : ""}
                    </p>
                    <p className="ins-tl-state">{ev.state}</p>
                    {ev.description && <p className="ins-tl-desc">{ev.description}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Regulatory partner footer */}
        {(footer.underwriter || footer.broker) && (
          <div className="ins-footer">
            {footer.underwriter && <div>Underwriter: {footer.underwriter}</div>}
            {footer.broker && <div>Broker: {footer.broker}</div>}
            {footer.note && <div>{footer.note}</div>}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  complaintDetail: state.complaints.complaintsDetail,
});
export default connect(mapStateToProps, { getInsuranceClaim })(InsuranceDetails);
